/**
 * ConfigManager - Standardized configuration loading for Page Editor
 *
 * Phase 5 Task 5.0: Config Bootstrap Standardization
 *
 * Provides a clean migration path from inline ERB globals to structured config:
 * 1. JSON script tag with id="page-editor-config" (preferred)
 * 2. window.PageEditorConfig object (bridge for transition)
 * 3. Inline CONFIG variable (legacy fallback)
 *
 * Usage:
 *   import { getConfig, getUrl, getPageContext } from './config_manager.js'
 *
 *   // Get full config
 *   const config = getConfig()
 *
 *   // Get specific URL
 *   const updateUrl = getUrl('updateBlock')
 *
 *   // Get page context (websiteId, pageId, adminId)
 *   const { websiteId, pageId, adminId } = getPageContext()
 *
 * JSON Script Tag Format (preferred):
 *   <script type="application/json" id="page-editor-config">
 *   {
 *     "websiteId": "uuid",
 *     "pageId": "uuid",
 *     "adminId": "uuid",
 *     "csrfToken": "token",
 *     "urls": {
 *       "updateBlock": "/admin/websites/:id/update_block_content",
 *       "updateHtml": "/admin/websites/:id/update_block_html",
 *       ...
 *     },
 *     "settings": {
 *       "ctaActionTypes": { ... }
 *     }
 *   }
 *   </script>
 */

import { createLogger } from './logger.js'

const logger = createLogger('ConfigManager')

// Cached config (loaded once)
let cachedConfig = null

// URL key mapping from legacy CONFIG keys to standardized names
const URL_KEY_MAP = {
  // Legacy key -> standardized key
  updateUrl: 'updateBlock',
  updateHtmlUrl: 'updateHtml',
  regenerateUrl: 'regenerate',
  regenerateContentUrl: 'regenerateContent',
  editBlockUrl: 'editBlock',
  rollbackUrl: 'rollback',
  blockHistoryUrl: 'blockHistory',
  pageHistoryUrl: 'pageHistory',
  blockTemplatesUrl: 'blockTemplates',
  replaceBlockTemplateUrl: 'replaceBlockTemplate',
  imageStylesUrl: 'imageStyles',
  imageProvidersUrl: 'imageProviders',
  generateImageUrl: 'generateImage',
  aiModelsUrl: 'aiModels',
  aiPromptTemplatesUrl: 'aiPromptTemplates',
  updateElementOverrideUrl: 'updateElementOverride',
  toggleElementVisibilityUrl: 'toggleElementVisibility',
  reorderElementsUrl: 'reorderElements'
}

/**
 * Load configuration from available sources (priority order)
 * @private
 */
function loadConfig() {
  // 1. Try JSON script tag (preferred)
  const scriptTag = document.getElementById('page-editor-config')
  if (scriptTag) {
    try {
      const config = JSON.parse(scriptTag.textContent)
      logger.log('Config loaded from JSON script tag')
      return normalizeConfig(config, 'script')
    } catch (e) {
      logger.error('Failed to parse config script tag:', e)
    }
  }

  // 2. Try window.PageEditorConfig (bridge object)
  if (typeof window !== 'undefined' && window.PageEditorConfig) {
    logger.log('Config loaded from window.PageEditorConfig')
    return normalizeConfig(window.PageEditorConfig, 'window')
  }

  // 3. Try inline CONFIG variable (legacy)
  // eslint-disable-next-line no-undef
  if (typeof window !== 'undefined' && typeof CONFIG !== 'undefined') {
    logger.log('Config loaded from inline CONFIG variable (legacy)')
    // eslint-disable-next-line no-undef
    return normalizeConfig(CONFIG, 'legacy')
  }

  // 4. No config found - return empty structure
  logger.warn('No config source found, using empty config')
  return {
    websiteId: null,
    pageId: null,
    adminId: null,
    csrfToken: null,
    urls: {},
    settings: {},
    _source: 'empty'
  }
}

/**
 * Normalize config from various sources into standard structure
 * @private
 */
function normalizeConfig(raw, source) {
  // If already in new format (has urls object)
  if (raw.urls && typeof raw.urls === 'object') {
    return {
      websiteId: raw.websiteId || null,
      pageId: raw.pageId || null,
      adminId: raw.adminId || null,
      csrfToken: raw.csrfToken || null,
      urls: raw.urls,
      settings: raw.settings || {},
      _source: source
    }
  }

  // Convert from legacy format (flat URL keys)
  const urls = {}
  const settings = {}

  for (const [key, value] of Object.entries(raw)) {
    // Map URL keys
    if (URL_KEY_MAP[key]) {
      urls[URL_KEY_MAP[key]] = value
    } else if (key === 'ctaActionTypes') {
      // Handle settings
      settings.ctaActionTypes = value
    }
  }

  return {
    websiteId: raw.websiteId || null,
    pageId: raw.pageId || null,
    adminId: raw.adminId || null,
    csrfToken: raw.csrfToken || null,
    urls,
    settings,
    _source: source
  }
}

/**
 * Get the full configuration object
 * @returns {Object} Configuration object
 */
export function getConfig() {
  if (!cachedConfig) {
    cachedConfig = loadConfig()
  }
  return cachedConfig
}

/**
 * Get a specific URL by key
 * Supports both legacy keys (updateUrl) and new keys (updateBlock)
 *
 * @param {string} key - URL key (e.g., 'updateBlock' or 'updateUrl')
 * @returns {string|null} URL or null if not found
 */
export function getUrl(key) {
  const config = getConfig()

  // Try standardized key first
  if (config.urls[key]) {
    return config.urls[key]
  }

  // Try mapping from legacy key
  const mappedKey = URL_KEY_MAP[key]
  if (mappedKey && config.urls[mappedKey]) {
    return config.urls[mappedKey]
  }

  // Try reverse lookup (new key -> find in legacy)
  for (const [legacyKey, newKey] of Object.entries(URL_KEY_MAP)) {
    if (newKey === key && config.urls[legacyKey]) {
      return config.urls[legacyKey]
    }
  }

  logger.warn('URL not found for key:', key)
  return null
}

/**
 * Get page context (IDs needed for API calls)
 * @returns {Object} { websiteId, pageId, adminId }
 */
export function getPageContext() {
  const config = getConfig()
  return {
    websiteId: config.websiteId,
    pageId: config.pageId,
    adminId: config.adminId
  }
}

/**
 * Get CSRF token for API requests
 * Falls back to meta tag if not in config
 * @returns {string|null} CSRF token
 */
export function getCsrfToken() {
  const config = getConfig()

  // Try config first
  if (config.csrfToken) {
    return config.csrfToken
  }

  // Fall back to meta tag (standard Rails pattern)
  const metaTag = document.querySelector('meta[name="csrf-token"]')
  if (metaTag) {
    return metaTag.content
  }

  logger.warn('No CSRF token found')
  return null
}

/**
 * Get a setting value
 * @param {string} key - Setting key (e.g., 'ctaActionTypes')
 * @returns {*} Setting value or undefined
 */
export function getSetting(key) {
  const config = getConfig()
  return config.settings[key]
}

/**
 * Check if config is loaded from preferred source
 * @returns {boolean} True if using JSON script tag
 */
export function isUsingPreferredSource() {
  const config = getConfig()
  return config._source === 'script'
}

/**
 * Reset cached config (useful for testing or dynamic updates)
 */
export function resetConfig() {
  cachedConfig = null
  logger.log('Config cache reset')
}

/**
 * Initialize config from a data attribute on an element
 * Useful for Stimulus controller integration
 *
 * @param {HTMLElement} element - Element with data-page-editor-config attribute
 */
export function initFromElement(element) {
  const configAttr = element.dataset.pageEditorConfig
  if (configAttr) {
    try {
      const config = JSON.parse(configAttr)
      window.PageEditorConfig = config
      cachedConfig = null // Reset to reload
      logger.log('Config initialized from element data attribute')
    } catch (e) {
      logger.error('Failed to parse config from element:', e)
    }
  }
}

// Export URL_KEY_MAP for reference (useful for migration tooling)
export { URL_KEY_MAP }
